import type { PhaseType } from '~/models/phase'
import type { DesiredOutcome } from '~/models/project'
import type { ConditionBand, RouteRedirect } from '~/models/routeRecommendation'
import { ROUTE_RULE_IDS, type RouteRuleId } from '../version'

/**
 * The outcome-to-route mapping (D-071, closing L-012).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS IS THE TABLE FROM D-071, AND ONLY THAT TABLE.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * | Desired outcome        | ≥ 0.50         | 0.45 – 0.50 | < 0.45      |
 * |------------------------|----------------|-------------|-------------|
 * | Visible abs            | Fat loss       | Fat loss    | Maintenance |
 * | Lose belly fat         | Fat loss       | Fat loss    | Maintenance |
 * | Look better in clothes | Fat loss       | Fat loss    | Lean bulk   |
 * | Build muscle           | Fat loss first | Lean bulk   | Lean bulk   |
 * | Event, ≥ 8 weeks out   | Fat loss       | Prep        | Maintenance |
 * | Event, < 8 weeks out   | Maintenance    | Maintenance | Maintenance |
 * | Maintain physique      | Maintenance    | Maintenance | Maintenance |
 *
 * **No numeric literal appears here.** The bands arrive already resolved and
 * the eight-week gate is applied by the caller from `rules/thresholds.ts`, so
 * this file holds the routing decisions and none of the numbers behind them.
 *
 * **A mini cut is absent, and its absence is the decision** (D-071): at −1.0%
 * to −0.7% per week it is the most aggressive thing the engine can prescribe,
 * and a first-day snapshot lacks exactly the adherence, recovery and strength
 * history needed to judge whether someone can tolerate it. The phase itself is
 * unaffected — it stays implementable and choosable for a later project. There
 * is deliberately no runtime guard rejecting it: an unreachable branch cannot
 * be shown to fail, so the real net is `route.test.ts`, which drives every
 * outcome across every band and asserts what never comes out.
 */

export interface RouteChoice {
  readonly phase: PhaseType
  readonly ruleId: RouteRuleId
  readonly redirect: RouteRedirect | undefined
  /** Why this route. Never empty — a route with no reasoning is a menu. */
  readonly reasoning: readonly string[]
}

export interface RouteTableInput {
  readonly desiredOutcome: DesiredOutcome
  readonly band: ConditionBand
  /** Whole weeks to the event; undefined when the outcome is not event-driven. */
  readonly weeksToEvent: number | undefined
  readonly eventHasEnoughLeadTime: boolean
}

export function chooseRoute(input: RouteTableInput): RouteChoice {
  switch (input.desiredOutcome) {
    case 'visibleAbs':
    case 'loseBellyFat':
      return leannessOutcome(input.band, input.desiredOutcome)
    case 'lookBetterInClothes':
      return lookBetterInClothes(input.band)
    case 'buildMuscle':
      return buildMuscle(input.band)
    case 'prepareForEvent':
      return prepareForEvent(input)
    case 'maintainPhysique':
      return maintainPhysique()
  }
}

const FAT_LOSS_PRIORITY_REASON =
  'Your waist is at or above half your height, so reducing fat comes before anything else this plan could aim at.'

const MIDDLE_BAND_REASON =
  'Your waist is below half your height but not yet in the range where further fat loss stops being worthwhile.'

/**
 * "Visible abs" and "lose belly fat" — the same route in every band.
 *
 * Below the stop-cutting boundary this is the redirect D-071 names: further
 * cutting costs muscle for nothing, and the engine says so rather than
 * accepting the request.
 */
function leannessOutcome(band: ConditionBand, outcome: DesiredOutcome): RouteChoice {
  if (band !== 'leanEnough') {
    return {
      phase: 'fatLoss',
      ruleId: ROUTE_RULE_IDS.routedToFatLoss,
      redirect: undefined,
      reasoning: [
        band === 'fatLossPriority' ? FAT_LOSS_PRIORITY_REASON : MIDDLE_BAND_REASON,
        'Losing fat at a controlled rate is the route to what you described.',
      ],
    }
  }

  return {
    phase: 'maintenance',
    ruleId: ROUTE_RULE_IDS.fatLossRedirectedToMaintenance,
    redirect: {
      insteadOf: 'fatLoss',
      because:
        'Your waist is already well under half your height. Cutting further from here tends to cost muscle without changing much, so Project Sola is not planning one.',
    },
    reasoning: [
      'Your waist is below the point where Project Sola recommends losing more fat.',
      outcome === 'visibleAbs'
        ? 'Holding steady and training consistently does more for definition from here than eating less would.'
        : 'Holding steady and training consistently is the more useful next step.',
    ],
  }
}

/** The one leanness-flavoured outcome that becomes a lean bulk when lean. */
function lookBetterInClothes(band: ConditionBand): RouteChoice {
  if (band !== 'leanEnough') {
    return {
      phase: 'fatLoss',
      ruleId: ROUTE_RULE_IDS.routedToFatLoss,
      redirect: undefined,
      reasoning: [
        band === 'fatLossPriority' ? FAT_LOSS_PRIORITY_REASON : MIDDLE_BAND_REASON,
        'Losing fat changes how clothes fit faster than adding size would.',
      ],
    }
  }

  return {
    phase: 'leanBulk',
    ruleId: ROUTE_RULE_IDS.routedToLeanBulk,
    redirect: undefined,
    reasoning: [
      'Your waist is already well under half your height.',
      'Adding size slowly does more for how clothes fit from here than losing more would.',
    ],
  }
}

/** The redirect D-071 names first: a surplus at a high waist mostly adds fat. */
function buildMuscle(band: ConditionBand): RouteChoice {
  if (band === 'fatLossPriority') {
    return {
      phase: 'fatLoss',
      ruleId: ROUTE_RULE_IDS.buildMuscleRedirectedToFatLoss,
      redirect: {
        insteadOf: 'leanBulk',
        because:
          'Eating in a surplus at your current waist measurement tends to add more fat than muscle. Fat loss first makes the muscle you build afterwards easier to see and easier to keep.',
      },
      reasoning: [
        FAT_LOSS_PRIORITY_REASON,
        'This is a first step toward building muscle, not a change of goal.',
      ],
    }
  }

  return {
    phase: 'leanBulk',
    ruleId: ROUTE_RULE_IDS.routedToLeanBulk,
    redirect: undefined,
    reasoning: [
      band === 'middle' ? MIDDLE_BAND_REASON : 'Your waist is well under half your height.',
      'A slow, controlled surplus is the route to building muscle from here.',
    ],
  }
}

/**
 * The event routes, including the three cells D-071 derives rather than chooses.
 *
 * Under the minimum lead time every band returns maintenance, because the
 * alternative is a recommendation that invites exactly the crash behaviour
 * `phase-system.md` forbids. Below the stop-cutting boundary an event returns
 * maintenance too: an aesthetic preparation is a deficit, and recommending one
 * there would contradict the floor that says fat loss is no longer recommended.
 *
 * The consequence, stated plainly in D-071: **aesthetic preparation is only
 * ever recommended in the middle band with enough lead time.** That window is
 * narrow, and it is narrow on purpose.
 */
function prepareForEvent(input: RouteTableInput): RouteChoice {
  if (!input.eventHasEnoughLeadTime) {
    return {
      phase: 'maintenance',
      ruleId: ROUTE_RULE_IDS.eventTooCloseForPrep,
      redirect: {
        insteadOf: 'aestheticPrep',
        because:
          'There is not enough time before the date to prepare at a rate Project Sola is willing to recommend. Planning one anyway would mean pushing harder than is sensible.',
      },
      reasoning: [
        'Holding steady and staying consistent is the better use of the time left.',
        'You can start a preparation project for a later date once this one finishes.',
      ],
    }
  }

  if (input.band === 'fatLossPriority') {
    return {
      phase: 'fatLoss',
      ruleId: ROUTE_RULE_IDS.routedToFatLoss,
      redirect: undefined,
      reasoning: [
        FAT_LOSS_PRIORITY_REASON,
        'With time in hand before the date, ordinary fat loss is the route rather than a sharper preparation.',
      ],
    }
  }

  if (input.band === 'middle') {
    return {
      phase: 'aestheticPrep',
      ruleId: ROUTE_RULE_IDS.routedToAestheticPrep,
      redirect: undefined,
      reasoning: [
        MIDDLE_BAND_REASON,
        'There is enough time to prepare for the date at a conservative rate.',
      ],
    }
  }

  return {
    phase: 'maintenance',
    ruleId: ROUTE_RULE_IDS.eventLeanEnoughForPrep,
    redirect: {
      insteadOf: 'aestheticPrep',
      because:
        'A preparation is a deficit, and your waist is already below the point where Project Sola recommends losing more fat.',
    },
    reasoning: ['Arriving at the date rested and consistent will do more than a deficit would.'],
  }
}

function maintainPhysique(): RouteChoice {
  return {
    phase: 'maintenance',
    ruleId: ROUTE_RULE_IDS.routedToMaintenance,
    redirect: undefined,
    reasoning: [
      'You asked to hold where you are, and nothing in your measurements argues against it.',
      'Maintenance keeps weight stable while strength and routine carry on.',
    ],
  }
}

/**
 * The training-frequency floor (D-071).
 *
 * Applied after the table rather than inside it, because it is one rule that
 * governs every lean-bulk cell: a surplus without enough training to use it is
 * mostly fat gain. **The frequency is named as the reason** rather than the
 * route silently changing — the user can act on "train three times a week" and
 * cannot act on an unexplained substitution.
 */
export function applyTrainingFrequencyFloor(
  choice: RouteChoice,
  weeklySessions: number | undefined,
  minimumSessions: number,
): RouteChoice {
  if (choice.phase !== 'leanBulk') return choice
  if (weeklySessions !== undefined && weeklySessions >= minimumSessions) return choice

  return {
    phase: 'maintenance',
    ruleId: ROUTE_RULE_IDS.leanBulkFellBackToMaintenance,
    redirect: {
      insteadOf: 'leanBulk',
      because:
        weeklySessions === undefined
          ? 'A lean bulk needs a training frequency Project Sola does not know yet. Add how many sessions a week you realistically train and this can be revisited.'
          : `A lean bulk needs at least ${minimumSessions} sessions a week to be worth the extra food. You said ${weeklySessions}.`,
    },
    reasoning: [
      'Eating in a surplus without enough training to use it mostly adds fat.',
      'Maintenance holds your weight steady while you build the training habit.',
    ],
  }
}
